import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { save } from "./api-cronograma";
import { findAll as findAllAulas } from "./Aula/api-cronograma-aula";
import { findByTurma } from "../TDP/api-tdp";
import { findAll as findAllLocais } from "../Local/api-local";
import { timeFormat } from "../../util/DateUtil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../util/ToastUtil";
import { DIAS_SEMANA, mensagemDe } from "./cronogramaUtil";

const labelAula = (aula) => `${aula.ordemAula}ª aula · ${timeFormat(aula.horarioInicio)} - ${timeFormat(aula.horarioFim)}`;

/** Diálogo de cadastro/edição de uma aula no cronograma da turma. */
export default function CronogramaForm({ cronograma, turma, cronogramaData, onClose, onSaved }) {
  const [valores, setValores] = useState({});
  const [aulas, setAulas] = useState([]);
  const [tdps, setTdps] = useState([]);
  const [locais, setLocais] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const open = Boolean(cronograma);

  useEffect(() => {
    if (!cronograma) return;
    setValores({
      diaSemana: cronograma.diaSemana ?? null,
      codAula: cronograma.cronogramaAula?.codigo ?? "",
      codTdp: cronograma.tdp?.codigo ?? "",
      codLocal: cronograma.local?.codigo ?? "",
    });
  }, [cronograma]);

  useEffect(() => {
    if (!open) return;
    findAllAulas().then((data) => {
      if (Array.isArray(data)) setAulas([...data].sort((a, b) => (a.ordemAula ?? 0) - (b.ordemAula ?? 0)));
      else if (data?.error) errorMessage(mensagemDe(data));
    });
    findAllLocais().then((data) => {
      if (Array.isArray(data)) setLocais(data);
      else if (data?.error) errorMessage(mensagemDe(data));
    });
  }, [open]);

  useEffect(() => {
    if (!open || !turma?.codigo) return;
    findByTurma(turma.codigo).then((data) => {
      if (Array.isArray(data)) setTdps(data);
      else if (data?.error) errorMessage(mensagemDe(data));
    });
  }, [open, turma?.codigo]);

  const updateField = (name, value) => setValores((prevState) => ({ ...prevState, [name]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!valores.diaSemana) {
      errorToast("Selecione o dia da semana.");
      return;
    }
    if (!valores.codAula) {
      errorToast("Selecione a aula.");
      return;
    }
    if (!valores.codTdp) {
      errorToast("Selecione a disciplina/professor.");
      return;
    }
    const persist = {
      ...cronograma,
      diaSemana: valores.diaSemana,
      cronogramaAula: aulas.find((a) => a.codigo === valores.codAula),
      tdp: tdps.find((t) => t.codigo === valores.codTdp),
      local: locais.find((l) => l.codigo === valores.codLocal) ?? null,
      cronogramaData,
    };
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(mensagemDe(data, "Não foi possível salvar o cronograma."));
          return;
        }
        success("Salvo com sucesso.");
        onSaved(data, !cronograma.codigo);
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{cronograma?.codigo ? "Editar Aula do Cronograma" : "Adicionar Aula ao Cronograma"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} pt={1}>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Dia da semana *
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={valores.diaSemana}
                onChange={(event, value) => value && updateField("diaSemana", value)}
                sx={{ flexWrap: "wrap" }}
              >
                {DIAS_SEMANA.map((dia) => (
                  <ToggleButton key={dia.value} value={dia.value}>
                    {dia.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
            <TextField
              select
              size="small"
              fullWidth
              required
              label="Aula"
              value={valores.codAula ?? ""}
              onChange={(event) => updateField("codAula", event.target.value)}
            >
              {aulas.map((aula) => (
                <MenuItem key={aula.codigo} value={aula.codigo}>
                  {labelAula(aula)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              fullWidth
              required
              label="Disciplina / Professor"
              value={valores.codTdp ?? ""}
              onChange={(event) => updateField("codTdp", event.target.value)}
            >
              {tdps.map((tdp) => (
                <MenuItem key={tdp.codigo} value={tdp.codigo}>
                  {tdp.disciplina?.descricao}
                  {tdp.usuario ? ` · ${tdp.usuario.nome}` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              fullWidth
              label="Local"
              value={valores.codLocal ?? ""}
              onChange={(event) => updateField("codLocal", event.target.value)}
            >
              <MenuItem value="">
                <em>Sem local</em>
              </MenuItem>
              {locais.map((local) => (
                <MenuItem key={local.codigo} value={local.codigo}>
                  {local.descricao}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={isSaving}>
            Salvar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
